import fs from "fs";
import path from "path";
import type { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import React, { useCallback, useState } from "react";
import Layout from "@/components/LandingPage/Layout";
// Import critical components normally
import HeroSection from "@/components/LandingPage/HeroSection";
import StatsSection from "@/components/LandingPage/StatsSection";

// Dynamically import non-critical components with loading fallbacks
const SignUpNotifyFormModal = dynamic(
  () => import("@/components/LandingPage/SignUpNotifyForm"),
  { ssr: false }
);
const ExamSuccessSection = dynamic(
  () => import("@/components/LandingPage/ExamSuccessSection"),
  { ssr: false }
);
const PlatformSection = dynamic(
  () => import("@/components/LandingPage/PlatformSection"),
  { ssr: false }
);
const ExamPreparationSection = dynamic(
  () => import("@/components/LandingPage/ExamPreparationSection"),
  { ssr: false }
);
const TestimonialsSection = dynamic(
  () => import("@/components/LandingPage/TestimonialsSection"),
  { ssr: false }
);
const PartnerSection = dynamic(
  () => import("@/components/LandingPage/PartnerSection"),
  { ssr: false }
);
const CombinedStorySection = dynamic(
  () => import("@/components/LandingPage/CombinedStorySection"),
  { ssr: false }
);
const CTASection = dynamic(
  () => import("@/components/LandingPage/CTASection"),
  { ssr: false }
);

// Pre-launch gate: until June 10 2026, 6:00 PM PT (PDT = UTC-7, i.e. 01:00 UTC Jun 11) —
// one hour before the 7:00 PM PT launch webinar.
// "/" serves the static launch landing (public/launch.html) in place, keeping
// the URL at "/". From the launch moment onward, "/" renders the full Next.js
// landing page as normal. Evaluated server-side per request.
const LAUNCH_AT_UTC = Date.UTC(2026, 5, 11, 1, 0, 0);

export const getServerSideProps: GetServerSideProps = async ({ res }) => {
  if (Date.now() < LAUNCH_AT_UTC) {
    const html = fs.readFileSync(
      path.join(process.cwd(), "public", "launch.html"),
      "utf8"
    );
    res.setHeader("Content-Type", "text/html; charset=utf-8");
    res.write(html);
    res.end();
    return { props: {} };
  }
  return { props: {} };
};

export default function Home() {
  const [signUpNotify, setSignUpNotify] = useState<boolean>(false);
  const [formType, setFormType] = useState<'join' | 'notify'>('join');
  const [examInfo, setExamInfo] = useState<{ exam: string; examLevel?: string } | undefined>(undefined);

  const handleSignUpToNotify = useCallback((type: 'join' | 'notify' = 'join', examInfo?: { exam: string; examLevel?: string }) => {
    setFormType(type);
    setExamInfo(examInfo);
    setSignUpNotify((prev) => !prev);
  }, []);

  const getFormProps = () => {
    if (formType === 'notify') {
      return {
        title: "Be the First to Know",
        buttonText: "Notify Me",
        formType: "notify",
        examInfo
      };
    }
    return {
      title: "Sign Up to Notify",
      buttonText: "Sign Up to Notify",
      formType: "join",
      examInfo
    };
  };

  return (
    <Layout>
      {/* critical components */}
      <HeroSection handleSignUpToNotify={handleSignUpToNotify} />
      <StatsSection />

      {/* non-critical components */}
      <ExamSuccessSection handleSignUpToNotify={handleSignUpToNotify} />
      <PlatformSection />
      <ExamPreparationSection handleSignUpToNotify={handleSignUpToNotify} />
      <TestimonialsSection />
      <PartnerSection />
      <CombinedStorySection />
      <CTASection handleSignUpToNotify={handleSignUpToNotify} />
      {signUpNotify && (
        <SignUpNotifyFormModal
          signUpNotify={signUpNotify}
          handleSignUpToNotify={handleSignUpToNotify}
          {...getFormProps()}
        />
      )}
    </Layout>
  );
}
